import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { updatePassword, updateEmail } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { accountService } from '../../../services/accountService';
import { useNavigate } from 'react-router-dom';

const SecuritySection = ({ expandedSection, onAccordionChange, setSuccess, setError, currentUser, isMobile, userProfile }) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: ''
  });
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmDelete: false
  });
  const [loading, setLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(currentUser, passwordForm.newPassword);
      setSuccess('Mot de passe mis à jour avec succès!');
      setPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating password:', error);
      setError('Erreur lors de la mise à jour du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailForm.newEmail) {
      setError('Veuillez saisir un nouvel email');
      return;
    }

    setLoading(true);
    try {
      await updateEmail(currentUser, emailForm.newEmail);
      
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        email: emailForm.newEmail,
        updatedAt: new Date()
      });

      setSuccess('Email mis à jour avec succès!');
      setEmailDialogOpen(false);
      setEmailForm({ newEmail: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating email:', error);
      setError('Erreur lors de la mise à jour de l\'email');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteForm.password) {
      setError('Veuillez saisir votre mot de passe pour confirmer');
      return;
    }

    if (!deleteForm.confirmDelete) {
      setError('Veuillez cocher la case pour confirmer la suppression');
      return;
    }

    setLoading(true);
    try {
      // Vérifier d'abord si le compte peut être supprimé
      const canDelete = accountService.canDeleteAccount(userProfile);
      if (!canDelete.canDelete) {
        setError(canDelete.reason || 'Impossible de supprimer le compte');
        return;
      }

      // Supprimer le compte
      await accountService.deleteAccount(currentUser, userProfile);
      
      // Rediriger vers la page d'accueil
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        setError('Mot de passe incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Veuillez vous reconnecter avant de supprimer votre compte');
      } else {
        setError('Erreur lors de la suppression du compte: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#F5F5DC',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB'
  };

  const content = (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 4 } }}>
      {!isMobile && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary,
            mb: { xs: 2, md: 3 },
            fontSize: { xs: '1.125rem', md: '1.25rem' }
          }}
        >
          Sécurité
        </Typography>
      )}

      <Typography
        variant="body2"
        sx={{
          color: colors.secondary,
          mb: { xs: 2, md: 3 },
          lineHeight: 1.6,
          fontSize: { xs: '0.875rem', md: '1rem' }
        }}
      >
        Gérez vos informations de connexion
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{
            p: { xs: 2, md: 3 },
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            backgroundColor: colors.background
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                color: colors.primary,
                mb: 1,
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Email de connexion
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.secondary, 
                mb: 2,
                fontSize: { xs: '0.875rem', md: '1rem' },
                wordBreak: 'break-word'
              }}
            >
              {currentUser.email}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setEmailDialogOpen(true)}
              size="small"
              fullWidth
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                py: 1,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Modifier l'email
            </Button>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ 
            p: 3, 
            border: `1px solid ${colors.border}`, 
            borderRadius: 2,
            backgroundColor: colors.background
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                color: colors.primary,
                mb: 1,
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Mot de passe
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.secondary, 
                mb: 2,
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              ••••••••
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setPasswordDialogOpen(true)}
              size="small"
              fullWidth
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                py: 1,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Modifier le mot de passe
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Delete Account Section */}
      <Box sx={{ mt: { xs: 3, md: 4 } }}>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.875rem', md: '1rem' }
            }
          }}
        >
          Zone dangereuse - Cette action est irréversible
        </Alert>
        
        <Box sx={{
          p: { xs: 2, md: 3 },
          border: '2px solid #dc2626',
          borderRadius: 2,
          backgroundColor: '#fef2f2'
        }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: '#dc2626',
              mb: 1,
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            Supprimer mon compte
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#7f1d1d',
              mb: 2,
              fontSize: { xs: '0.875rem', md: '1rem' },
              lineHeight: 1.6
            }}
          >
            Cette action supprimera définitivement votre compte et toutes les données associées.
            {userProfile?.role === 'salon' && ' Toutes vos réservations futures seront annulées.'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            size="small"
            sx={{
              borderColor: '#dc2626',
              color: '#dc2626',
              py: 1,
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#b91c1c',
                backgroundColor: 'rgba(220, 38, 38, 0.04)'
              }
            }}
          >
            Supprimer mon compte
          </Button>
        </Box>
      </Box>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: colors.primary,
          fontSize: { xs: '1.125rem', md: '1.25rem' }
        }}>
          Modifier le mot de passe
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe actuel"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nouveau mot de passe"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                helperText="Minimum 6 caractères"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmer le nouveau mot de passe"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setPasswordDialogOpen(false)}
            sx={{ 
              color: colors.secondary,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained" 
            disabled={loading}
            sx={{
              backgroundColor: colors.primary,
              color: colors.surface,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#333333'
              }
            }}
          >
            {loading ? 'Mise à jour...' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => setEmailDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: colors.primary,
          fontSize: { xs: '1.125rem', md: '1.25rem' }
        }}>
          Modifier l'email
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email actuel"
                value={currentUser.email}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nouvel email"
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setEmailDialogOpen(false)}
            sx={{ 
              color: colors.secondary,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleEmailChange} 
            variant="contained" 
            disabled={loading}
            sx={{
              backgroundColor: colors.primary,
              color: colors.surface,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#333333'
              }
            }}
          >
            {loading ? 'Mise à jour...' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 600,
          color: '#dc2626',
          fontSize: { xs: '1.125rem', md: '1.25rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          Confirmer la suppression du compte
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Cette action est irréversible !
            </Typography>
            <Typography variant="body2">
              Votre compte et toutes les données associées seront définitivement supprimés.
              {userProfile?.role === 'salon' && ' Toutes vos réservations futures seront annulées et vos clients seront notifiés.'}
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmez votre mot de passe"
                type="password"
                value={deleteForm.password}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, password: e.target.value }))}
                helperText="Saisissez votre mot de passe pour confirmer la suppression"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#dc2626',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#dc2626',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteForm.confirmDelete}
                    onChange={(e) => setDeleteForm(prev => ({ ...prev, confirmDelete: e.target.checked }))}
                    sx={{
                      color: '#dc2626',
                      '&.Mui-checked': {
                        color: '#dc2626',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Je comprends que cette action est irréversible et je souhaite supprimer définitivement mon compte
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteForm({ password: '', confirmDelete: false });
            }}
            sx={{
              color: colors.secondary,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            disabled={loading || !deleteForm.password || !deleteForm.confirmDelete}
            startIcon={<DeleteIcon />}
            sx={{
              backgroundColor: '#dc2626',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              '&:disabled': {
                backgroundColor: '#9ca3af',
                color: '#ffffff'
              }
            }}
          >
            {loading ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (isMobile) {
    return (
      <Accordion 
        expanded={expandedSection === 'security'} 
        onChange={onAccordionChange('security')}
        elevation={0}
        sx={{ 
          '&:before': { display: 'none' },
          borderRadius: '0 !important'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            px: 3,
            py: 2,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              '&.Mui-expanded': {
                margin: 0
              }
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: colors.primary,
              fontSize: '1.125rem'
            }}
          >
            Sécurité
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {content}
        </AccordionDetails>
      </Accordion>
    );
  }

  return content;
};

export default SecuritySection;